/**
 * Heartbeats V2 rollout switch for the Analytics Lambda (DEV-5763).
 *
 * The switch is a path prefix, not an adapter. The V2 endpoints mirror V1's
 * request and response contract on these five routes, and any `external_action`
 * V2 does not compute itself is delegated back to V1 unchanged -- so the quizzes,
 * labs and activities tabs that share `/series` and `/aggregation` keep working
 * and keep returning the same numbers. That is why the entire V1/V2 selection
 * lives here plus the five call sites in `services/reporting.ts`, and why no
 * component branches on the flag.
 *
 * The flag is evaluated in React (`useFlags`) and mirrored into this module
 * because the reporting services are plain functions, not hooks. See
 * `contexts/launchDarkly/HeartbeatsV2FlagSync.tsx` for why the mirror is written
 * during render rather than in an effect.
 *
 * The switch fails closed in every direction: flag missing from LaunchDarkly,
 * flag present but not a boolean, or mirror never synced all resolve to V1.
 * That matters more than it looks -- a rollout switch that failed open would
 * serve V2 to every account owner at once.
 */

/**
 * The LaunchDarkly key, camelCase so that the flag is spelled identically in
 * LaunchDarkly and here. The React SDK camelCases keys as it exposes them, so a
 * kebab-case key would arrive under a second, different name and the two
 * spellings would have to be kept in sync by hand.
 */
export const HEARTBEATS_V2_DASHBOARD_ANALYTICS_FLAG = 'heartbeatsV2DashboardAnalytics';

const V2_PREFIX = 'v2/';

let heartbeatsV2Enabled = false;

/** Mirrors the LaunchDarkly value. Anything that is not exactly `true` is V1. */
export const setHeartbeatsV2Enabled = (value: unknown): void => {
  heartbeatsV2Enabled = value === true;
};

export const isHeartbeatsV2Enabled = (): boolean => heartbeatsV2Enabled;

/** Visible for tests, so one case cannot leak its flag state into the next. */
export const resetHeartbeatsV2Enabled = (): void => {
  heartbeatsV2Enabled = false;
};

/**
 * Returns the route to call for a heartbeat-backed analytics endpoint: the V1
 * route as-is, or the same route under `v2/`.
 *
 * Only the five heartbeat routes are passed through this helper. Routes that are
 * not backed by heartbeats (`active-users`, `preferred-content`, and the labs,
 * quizzes and activities tables, all of which the Lambda answers from the
 * Content API rather than from watch-time data) have no V2 counterpart and must
 * keep calling V1.
 */
export const heartbeatsRoute = (route: string): string =>
  heartbeatsV2Enabled ? `${V2_PREFIX}${route.replace(/^\/+/, '')}` : route;
